import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Title from "@/components/Title";
import { QueryTable } from "@/components/query-table/QueryTable";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { SuccessFetchListResponse } from "@/services/api";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { toast } from "@/components/ui/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import useQueryData from "@/hooks/useQueryData";
import { DataTableQueryContext } from "@/components/data-table/DataTableQueryContext";
import { costsService } from "@/services/costs";
import { CostFormData, ReadCosts, UpdateCosts } from "@/types/costs";
import { CostsForm } from "./CostsForm";
import { costsFormSchema, CostsFormSchemaType } from "@/schemas/costsForm";
import { costsReadSchema } from "@/schemas/costs";
import { useCostsColumnOverrides } from "../hooks/useCostsColumnOverrides";
import { useFormBaseline } from "@/hooks/useFormBaseline";
import { useCostsFormDefaultValues } from "../hooks/useCostsFormDefaultValues";
import { AddCostsDialog } from "./AddCostsDialog";

interface CostsSectionComponentProps {
  invoiceSpecificationId: string;
}

const CostsSectionComponent = ({
  invoiceSpecificationId,
}: CostsSectionComponentProps) => {
  const [isAddCostsDialogOpen, setIsAddCostsDialogOpen] = useState(false);
  const name = "costs";
  const nameForTranslations = "costs";
  const { t } = useTranslation();

  const [watchedCosts, setWatchedCosts] = useState<
    CostsFormSchemaType["costs"] | undefined
  >();

  const options = useCreateDataTableOptions<ReadCosts>(name);

  const costsListQueryKey = useDynamicQueryParams(name, options);
  const costsListFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      costsService.getList(invoiceSpecificationId, {
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    [invoiceSpecificationId, options.filters, options.sorting, costsService],
  );

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<ReadCosts>) => lastPage.last_item,
    [],
  );

  const query = useInfiniteQuery({
    queryKey: costsListQueryKey,
    queryFn: costsListFn,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
  const queryValue = useMemo(() => ({ query }), [query]);
  const costsReadData = useQueryData(queryValue.query) || [];
  const defaultValues = useCostsFormDefaultValues(costsReadData);

  const costsForm = useForm<CostsFormSchemaType>({
    resolver: zodResolver(costsFormSchema),
    defaultValues,
    disabled: query.isFetching,
  });

  // Sync watched costs with default values when data changes
  useEffect(() => {
    setWatchedCosts(defaultValues.costs);
  }, [defaultValues]);

  const handleIncludeChange = useCallback(
    (costs: CostsFormSchemaType["costs"]) => {
      setWatchedCosts(() => ({ ...costs }));
    },
    [],
  );

  const getRowProps = useCallback(
    function <T>(row: { original: T }): Record<string, unknown> {
      const original = row.original as unknown as {
        id: string;
      };
      const id = original.id;
      // Since API doesn't return include field, use form state or default to true (included by default)
      const isIncluded = watchedCosts
        ? watchedCosts[id]?.include ?? true
        : true;

      return {
        className: isIncluded
          ? "bg-green-50 hover:bg-green-100"
          : "bg-red-50 hover:bg-red-100",
      };
    },
    [watchedCosts],
  );

  const columnOverrides = useCostsColumnOverrides();

  const { baseline, updateBaseline } = useFormBaseline(
    costsForm,
    defaultValues,
  );

  const tableFeatures = useMemo(
    () => ({
      editionHighlight: { baseline, recordKey: "costs" },
      enableFilters: false,
      enableSorting: false,
      getRowProps,
    }),
    [baseline, getRowProps],
  );

  const editCosts = async (values: UpdateCosts) => {
    await costsService.patch(invoiceSpecificationId, values);
  };

  const queryClient = useQueryClient();

  const editCostsMutation = useMutationWithErrorHandling({
    mutationFn: editCosts,
    onSuccess: () => {
      updateBaseline();
      toast({ title: t("costs.success.update") });
      queryClient.invalidateQueries({
        queryKey: costsListQueryKey,
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes(invoiceSpecificationId) ||
          query.queryKey.includes("invoiceSpecification"),
      });
    },
    errorTitle: t("costs.error.update"),
  });

  const handleEdit = useCallback(
    async (
      values: CostsFormSchemaType["costs"],
      dirtyFields: Record<string, unknown>,
    ) => {
      const patchData: UpdateCosts = Object.entries(dirtyFields).reduce(
        (acc, [key, isDirty]) => {
          if (isDirty && values[key]) {
            const { include, ...data } = values[key];
            acc = include
              ? {
                  ...acc,
                  costs: [...(acc.costs || []), data],
                }
              : {
                  ...acc,
                  costs_delete: [...(acc.costs_delete || []), key],
                };
          }

          return acc;
        },
        {} as NonNullable<UpdateCosts>,
      );

      await editCostsMutation.mutateAsync(patchData);
    },
    [editCostsMutation],
  );

  const addCostMutation = useMutationWithErrorHandling({
    mutationFn: (cost: CostFormData) => {
      return costsService.patch(invoiceSpecificationId, {
        costs: [cost],
      });
    },
    errorTitle: t("costs.errors.create"),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: costsListQueryKey,
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes(invoiceSpecificationId) ||
          query.queryKey.includes("invoiceSpecification"),
      });
      toast({
        title: t("costs.success.create"),
      });
      setIsAddCostsDialogOpen(false);
    },
  });

  const handleAddCostFormSubmit = useCallback(
    (data: CostFormData) => {
      addCostMutation.mutate(data);
    },
    [invoiceSpecificationId],
  );

  return (
    <>
      <QueryTable
        schema={costsReadSchema}
        name={nameForTranslations}
        query={query}
        options={options}
        tableFeatures={tableFeatures}
        columnOverrides={columnOverrides}
      >
        <div className="py-2">
          <Title>{t("costs.header")}</Title>
        </div>
        <DataTableQueryContext.Provider value={queryValue}>
          <CostsForm
            onIncludeChange={handleIncludeChange}
            onEdit={handleEdit}
            form={costsForm}
            defaultValues={defaultValues}
            onAddClick={() => setIsAddCostsDialogOpen(true)}
          />
        </DataTableQueryContext.Provider>
      </QueryTable>
      <AddCostsDialog
        isOpen={isAddCostsDialogOpen}
        isSubmitting={addCostMutation.isPending}
        onSubmit={handleAddCostFormSubmit}
        onClose={() => setIsAddCostsDialogOpen(false)}
      />
    </>
  );
};

export const CostsSection = memo(CostsSectionComponent);
